(() => {
  const purposes = ['preferences', 'analytics', 'marketing'];
  function consentPayload(mode, choices = {}) {
    return Object.fromEntries(purposes.map(key => [key, mode === 'accept' || (mode === 'save' && choices[key] === true)]));
  }
  function hasDecision(consent = {}) {
    return purposes.every(key => consent[key] === 'yes' || consent[key] === 'no');
  }
  function selectedChoices(consent = {}) {
    return Object.fromEntries(purposes.map(key => [key, consent[key] === 'yes']));
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {consentPayload, hasDecision, selectedChoices};
  if (typeof document === 'undefined') return;
  const root = document.querySelector('[data-qcp-root]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const banner = root.querySelector('[data-qcp-banner]');
  const dialog = root.querySelector('dialog');
  const form = root.querySelector('[data-qcp-form]');
  const choices = Array.from(root.querySelectorAll('[data-qcp-choice]'));
  const saveButtons = Array.from(root.querySelectorAll('[data-qcp-accept],[data-qcp-reject],[data-qcp-save]'));
  const statusNodes = Array.from(root.querySelectorAll('[data-qcp-status],[data-qcp-dialog-status]'));
  const previewBanner = new URLSearchParams(window.location.search).get('ver_cookies') === '1';
  let api, busy = false, previousFocus, loadAttempt = 0, saveTimeout, featureRequested = false, decidedThisVisit = false;
  function status(text) { statusNodes.forEach(node => { node.textContent = text; }); }
  function enabled(value) {
    saveButtons.forEach(button => { button.disabled = !value; });
    choices.forEach(input => { input.disabled = !value; });
  }
  function updatePreviewOffset() {
    const frame = document.querySelector('iframe#preview-bar-iframe,iframe[src*="preview-bar"]');
    const height = frame ? frame.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--qcp-preview-bottom', Math.min(height, 100) + 'px');
  }
  function current() {
    try { return api.currentVisitorConsent(); } catch (_) { return {}; }
  }
  function reflectConsent() {
    const consent = current();
    const selected = selectedChoices(consent);
    choices.forEach(input => { input.checked = selected[input.dataset.qcpChoice]; });
    purposes.forEach(key => { root.dataset[key] = consent[key] || ''; });
    return consent;
  }
  function syncGoogle() {
    if (typeof window.gtag !== 'function') return;
    const consent = current();
    const allowed = (key, method) => consent[key] === 'yes' && typeof api[method] === 'function' && api[method]();
    const marketing = allowed('marketing', 'marketingAllowed') ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      analytics_storage: allowed('analytics', 'analyticsProcessingAllowed') ? 'granted' : 'denied',
      ad_storage: marketing,
      ad_user_data: marketing,
      ad_personalization: marketing,
      functionality_storage: allowed('preferences', 'preferencesProcessingAllowed') ? 'granted' : 'denied',
      personalization_storage: allowed('preferences', 'preferencesProcessingAllowed') ? 'granted' : 'denied'
    });
  }
  function openPreferences() {
    previousFocus = document.activeElement;
    if (api && !busy) reflectConsent();
    if (!dialog.open) dialog.showModal();
    if (!api) status(root.dataset.loading);
  }
  function closePreferences() {
    dialog.close();
    if (previousFocus && previousFocus.isConnected) previousFocus.focus({preventScroll:true});
  }
  function failSave() {
    clearTimeout(saveTimeout);
    busy = false;
    root.removeAttribute('aria-busy');
    enabled(Boolean(api));
    status(root.dataset.error);
    banner.hidden = false;
  }
  function save(mode) {
    if (!api || busy) return;
    const selected = Object.fromEntries(choices.map(input => [input.dataset.qcpChoice, input.checked]));
    const payload = consentPayload(mode, selected);
    busy = true;
    root.setAttribute('aria-busy', 'true');
    enabled(false);
    status(root.dataset.loading);
    saveTimeout = setTimeout(failSave, 7000);
    try {
      api.setTrackingConsent(payload, () => {
        const stored = current();
        if (!purposes.every(key => stored[key] === (payload[key] ? 'yes' : 'no'))) { failSave(); return; }
        clearTimeout(saveTimeout);
        busy = false;
        root.removeAttribute('aria-busy');
        reflectConsent();
        syncGoogle();
        enabled(true);
        decidedThisVisit = true;
        banner.hidden = true;
        status(root.dataset.saved);
        if (dialog.open) closePreferences();
      });
    } catch (_) { failSave(); }
  }
  function ready() {
    if (root.dataset.ready === 'true') return;
    api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.setTrackingConsent !== 'function') { loadFailed(); return; }
    root.dataset.ready = 'true';
    document.documentElement.classList.add('qroses-consent-ready');
    root.querySelector('[data-qcp-retry]').hidden = true;
    enabled(true);
    const consent = reflectConsent();
    banner.hidden = hasDecision(consent) && (!previewBanner || decidedThisVisit);
    status('');
    syncGoogle();
    updatePreviewOffset();
    setTimeout(updatePreviewOffset, 700);
    setTimeout(updatePreviewOffset, 2000);
  }
  function loadFailed() {
    api = undefined;
    enabled(false);
    banner.hidden = false;
    root.querySelector('[data-qcp-retry]').hidden = false;
    status(root.dataset.error);
  }
  function loadApi() {
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy && typeof shopify.customerPrivacy.setTrackingConsent === 'function') { ready(); return; }
    if (++loadAttempt >= 40) { loadFailed(); return; }
    if (shopify && typeof shopify.loadFeatures === 'function' && !featureRequested) {
      featureRequested = true;
      // Use the same native feature version as the store's existing consent bridge.
      // Poll independently because concurrent native feature loads can skip callbacks.
      shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], () => {
        if (shopify.customerPrivacy && typeof shopify.customerPrivacy.setTrackingConsent === 'function') ready();
      });
    }
    setTimeout(loadApi, 200);
  }
  document.querySelectorAll('[data-qcp-open]').forEach(button => button.addEventListener('click', openPreferences));
  root.querySelector('[data-qcp-close]').addEventListener('click', closePreferences);
  root.querySelectorAll('[data-qcp-accept]').forEach(button => button.addEventListener('click', () => save('accept')));
  root.querySelectorAll('[data-qcp-reject]').forEach(button => button.addEventListener('click', () => save('reject')));
  root.querySelector('[data-qcp-retry]').addEventListener('click', () => { loadAttempt = 0; featureRequested = false; status(root.dataset.loading); loadApi(); });
  form.addEventListener('submit', event => { event.preventDefault(); save('save'); });
  document.addEventListener('visitorConsentCollected', () => {
    if (!api) return;
    if (!busy) { const consent = reflectConsent(); banner.hidden = hasDecision(consent) && (!previewBanner || decidedThisVisit); }
    syncGoogle();
  });
  window.addEventListener('resize', updatePreviewOffset);
  window.addEventListener('pageshow', () => { if (api && !busy) { banner.hidden = hasDecision(reflectConsent()) && (!previewBanner || decidedThisVisit); syncGoogle(); } });
  loadApi();
})();
